import React, { useEffect, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { BASE_CODES } from "./CurrencyMasterDashboard";
import { ArrowRight, Repeat } from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
  { title: "Common daily conversions" },
  { title: "Common biweekly conversions" },
  { title: "Common monthly conversions" },
];
const LINES = 7;
const emptyLines = () => Array.from({ length: LINES }, () => ({ from: "", to: "", rate: "" }));
const fmt4 = (v) => { const n = parseFloat(v); return isNaN(n) ? "" : n.toFixed(4); };

export default function CurrencyConversionBoard() {
  const [board, setBoard] = useState(GROUPS.map(() => emptyLines()));
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const opt = await client.get("/options/currency");
        const saved = opt.data?.conversion_board;
        if (Array.isArray(saved) && saved.length) {
          setBoard(GROUPS.map((_, g) => {
            const lines = Array.isArray(saved[g]) ? saved[g] : [];
            return Array.from({ length: LINES }, (_, i) => ({
              from: lines[i]?.from || "", to: lines[i]?.to || "", rate: lines[i]?.rate ?? "", mark: lines[i]?.mark || "",
            }));
          }));
        }
      } catch (e) { /* noop */ } finally { setLoaded(true); }
    })();
  }, []);

  const persist = async (next) => {
    try { await client.put("/options/currency/conversion_board", { values: next }); } catch (e) { /* noop */ }
  };
  const setLine = (g, i, patch, save = false) => {
    setBoard((b) => {
      const next = b.map((grp, gi) => gi === g ? grp.map((ln, li) => li === i ? { ...ln, ...patch } : ln) : grp);
      if (save) persist(next);
      return next;
    });
  };
  const setMark = (g, i, mark) => {
    setBoard((b) => {
      const cur = b[g]?.[i]?.mark || "";
      const turningOn = cur !== mark;
      if (turningOn && !cur) {
        const flagged = b.flat().filter((l) => l.mark).length;
        if (flagged >= 3) { toast.error(TL("Flag limit reached: only 3 rates can be flagged (Base or Inverted) at once.")); return b; }
      }
      const next = b.map((grp, gi) => gi === g ? grp.map((ln, li) => li === i ? { ...ln, mark: cur === mark ? "" : mark } : ln) : grp);
      persist(next).then(() => window.dispatchEvent(new CustomEvent("fopfo:flags-changed")));
      return next;
    });
  };
  const applyTo = (card, r) => {
    if (!r.from || !r.to || !(parseFloat(r.rate) > 0)) { toast.error("Fill this line's From, To and a rate before applying."); return; }
    window.dispatchEvent(new CustomEvent("fopfo:apply-rate", { detail: { card, rate: r } }));
    toast.success(`${r.from}→${r.to} @ ${r.rate} applied to the ${card === "instant" ? "Instant" : "Rule"} transfer card.`);
  };
  const flagged = board.flat().filter((l) => l && (l.mark === "inverted" || l.mark === "base")).slice(0, 3).map((l) =>
    l.mark === "inverted"
      ? { from: l.to, to: l.from, rate: parseFloat(l.rate) > 0 ? (1 / parseFloat(l.rate)).toFixed(4) : "", kind: "inverted" }
      : { from: l.from, to: l.to, rate: l.rate, kind: "base" }
  );

  return (
    <div className="mb-3 rounded-xl border border-black/10 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.03]" data-testid="currency-conversion-board">
      <div className="mb-1 flex items-center gap-2">
        <Repeat size={16} className="text-[var(--primary)]" />
        <h3 className="font-head text-base">{TL("Currency Conversions — at a glance")}</h3>
      </div>
      {!loaded ? (
        <div className="py-6 text-center text-sm opacity-60" data-testid="cb-loading">{TL("Loading…")}</div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-3">
          <style>{`
            @keyframes cbFY { 0%,100%{background:transparent} 50%{background:rgba(250,204,21,.6)} }
            @keyframes cbFR { 0%,100%{background:transparent} 50%{background:rgba(239,68,68,.5)} }
            .cb-flash-yellow{animation:cbFY 1s ease-in-out infinite}
            .cb-flash-red{animation:cbFR 1s ease-in-out infinite}
          `}</style>
          {GROUPS.map((grp, g) => (
            <div key={g} className="rounded-lg border border-black/10 dark:border-white/10" data-testid={`cb-group-${g}`}>
              <div className="border-b border-black/10 bg-[var(--primary)]/10 px-2 py-1.5 text-center text-xs font-semibold dark:border-white/10">{TL(grp.title)}</div>
              <div className="p-2">
                <div className="mb-1 flex items-center gap-1 px-0.5 text-[9px] font-semibold uppercase tracking-wide opacity-50">
                  <span className="w-16">{TL("From")}</span>
                  <span className="w-20 text-center">{TL("Rate")}</span>
                  <span className="w-3" />
                  <span className="w-16">{TL("To")}</span>
                  <span className="w-20 text-center">{TL("Inverted")}</span>
                  <span className="ms-auto w-[92px] text-center">{TL("Select")}</span>
                </div>
                <div className="space-y-1">
                  {board[g].map((ln, i) => {
                    const inv = parseFloat(ln.rate) > 0 ? (1 / parseFloat(ln.rate)).toFixed(4) : "";
                    return (
                    <div key={i} className={`flex items-center gap-1 rounded ${ln.mark === "inverted" ? "cb-flash-red" : ln.mark === "base" ? "cb-flash-yellow" : ""}`} data-testid={`cb-line-${g}-${i}`}>
                      <select value={ln.from} onChange={(e) => setLine(g, i, { from: e.target.value }, true)} data-testid={`cb-from-${g}-${i}`}
                        className="w-16 flex-none rounded border border-black/15 bg-white/70 px-1 py-1 text-xs dark:border-white/15 dark:bg-black/30">
                        <option value="">—</option>
                        {BASE_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="number" step="0.0001" value={ln.rate} placeholder="0.0000"
                        onChange={(e) => setLine(g, i, { rate: e.target.value })}
                        onBlur={(e) => setLine(g, i, { rate: fmt4(e.target.value) }, true)}
                        data-testid={`cb-rate-${g}-${i}`}
                        className="w-20 flex-none rounded border border-black/10 bg-white/60 px-1 py-1 text-center text-xs tabular-nums dark:border-white/10 dark:bg-black/30" />
                      <ArrowRight size={12} className="w-3 flex-none opacity-40" />
                      <select value={ln.to} onChange={(e) => setLine(g, i, { to: e.target.value }, true)} data-testid={`cb-to-${g}-${i}`}
                        className="w-16 flex-none rounded border border-black/15 bg-white/70 px-1 py-1 text-xs dark:border-white/15 dark:bg-black/30">
                        <option value="">—</option>
                        {BASE_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input readOnly value={inv} placeholder="—" title={TL("Inverted rate (auto: 1 ÷ Rate)")} data-testid={`cb-inverted-rate-${g}-${i}`}
                        className="w-20 flex-none rounded border border-black/10 bg-black/5 px-1 py-1 text-center text-xs tabular-nums opacity-80 dark:border-white/10 dark:bg-white/10" />
                      <div className="ms-auto flex w-[92px] flex-none justify-center gap-0.5">
                        <button type="button" onClick={() => setMark(g, i, "base")} data-testid={`cb-base-${g}-${i}`}
                          title={TL("Flag the BASE rate (as entered) — flashes yellow")}
                          className={`rounded px-2 py-1 text-[10px] font-semibold ${ln.mark === "base" ? "bg-yellow-400 text-black" : "border border-black/15 opacity-70 dark:border-white/15"}`}>{TL("Base")}</button>
                        <button type="button" onClick={() => setMark(g, i, "inverted")} data-testid={`cb-inverted-${g}-${i}`}
                          title="Flag the INVERTED rate — flashes red; available in Transfer & convert"
                          className={`rounded px-2 py-1 text-[10px] font-semibold ${ln.mark === "inverted" ? "bg-red-500 text-white" : "border border-black/15 opacity-70 dark:border-white/15"}`}>{TL("Inverted")}</button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {flagged.length > 0 && (
        <div className="mt-3 rounded-lg border border-yellow-400/50 bg-yellow-400/10 p-2.5" data-testid="cb-flagged-apply">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide opacity-60">{TL("Apply a flagged rate to a Transfer card")}</div>
          <div className="space-y-1.5">
            {flagged.map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 rounded border border-black/10 bg-white/60 px-2 py-1 text-xs dark:border-white/10 dark:bg-black/20" data-testid={`cb-flagged-row-${i}`}>
                <span className={`h-2 w-2 flex-none rounded-full ${r.kind === "inverted" ? "bg-red-500" : "bg-yellow-400"}`} />
                <b className="tabular-nums">{r.from || "?"}→{r.to || "?"} @ {r.rate || "0"}</b>
                <span className="opacity-60">{r.kind === "inverted" ? TL("inverted") : TL("base")}</span>
                <span className="ms-auto flex items-center gap-1">
                  <span className="text-[10px] opacity-60">{TL("Apply to")}</span>
                  <button type="button" onClick={() => applyTo("instant", r)} data-testid={`cb-apply-instant-${i}`}
                    className="rounded bg-[var(--primary)]/15 px-2 py-1 text-[10px] font-semibold text-[var(--primary)] hover:bg-[var(--primary)]/25">{TL("Instant")}</button>
                  <button type="button" onClick={() => applyTo("rules", r)} data-testid={`cb-apply-rule-${i}`}
                    className="rounded bg-[var(--primary)]/15 px-2 py-1 text-[10px] font-semibold text-[var(--primary)] hover:bg-[var(--primary)]/25">{TL("Rule")}</button>
                </span>
              </div>
            ))}
          </div>
          <p className="mt-1.5 text-[10px] opacity-50">{TL("Flag up to 3 rates (Base / Inverted) above, then send each to the card you want — the card switches to “Transfer & convert” with that rate pre-selected. Unapplied flags are ignored.")}</p>
        </div>
      )}
      <p className="mt-3 text-[11px] leading-relaxed opacity-60" data-testid="cb-rate-source-note">
        {TL("Rate source: rates are entered and maintained manually by the User — there is no automatic internet feed. Update them whenever your reference rates change: many desks refresh once daily around 16:00 CET (when the European Central Bank publishes reference rates); weekly or monthly is fine for less-active pairs. Caveat: not all currencies have a public market rate (e.g. pegged or restricted currencies such as KPW), so those must be entered by hand. GCA accepts and assumes no responsibility and specifically denies any responsibility for the rates shown or entered, as they are only for client convenience, and Client is responsible for verifying all rates.")}
      </p>
    </div>
  );
}
